import { z } from "zod";

/** UUID identifier (all primary/foreign keys are uuids). */
export const Uuid = z.uuid();
export type Uuid = z.infer<typeof Uuid>;

/** Alias — entity id. */
export const Id = Uuid;
export type Id = z.infer<typeof Id>;

/** Calendar date as an ISO 8601 string, e.g. "2026-07-17". */
export const IsoDate = z.iso.date();
export type IsoDate = z.infer<typeof IsoDate>;

/** Timestamp as an ISO 8601 string, e.g. "2026-07-17T09:30:00Z" (offsets allowed). */
export const IsoDateTime = z.iso.datetime({ offset: true });
export type IsoDateTime = z.infer<typeof IsoDateTime>;

/**
 * Money value. Stored as numeric(12,4) in Postgres (source nett prices carry
 * 4 decimal places); transported as a plain JSON number.
 */
export const Money = z.number();
export type Money = z.infer<typeof Money>;

/**
 * Audit stamp fields present on every persisted entity (and therefore on every
 * `*Dto`). Spread `stamps` into a `z.object({...})`, or `.extend(stamps)`.
 * `createdBy`/`updatedBy` are nullable to accommodate seed/system writes.
 */
export const stamps = {
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  createdBy: Uuid.nullable(),
  updatedBy: Uuid.nullable(),
};

/** Object schema form of {@link stamps}. */
export const Stamps = z.object(stamps);
export type Stamps = z.infer<typeof Stamps>;

/**
 * Generic list-endpoint envelope: `{ items: T[], total: number }`.
 * Usage: `ListResponse(SupplierDto)` for the schema, `ListResponse<Supplier>` for the type.
 */
export const ListResponse = <T extends z.ZodType>(item: T) =>
  z.object({
    items: z.array(item),
    total: z.number().int().nonnegative(),
  });
export type ListResponse<T> = { items: T[]; total: number };

/** Standard error envelope: `{ error: { message, code? } }`. */
export const ApiError = z.object({
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
  }),
});
export type ApiError = z.infer<typeof ApiError>;

/** Common `?limit=&offset=` query params (coerced from strings). */
export const PaginationQuery = z.object({
  limit: z.coerce.number().int().min(1).max(500).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type PaginationQuery = z.infer<typeof PaginationQuery>;

/** `{ ok: true }` responses (health, logout). */
export const OkResponse = z.object({ ok: z.literal(true) });
export type OkResponse = z.infer<typeof OkResponse>;
